// externals
import type { FC } from 'react';
import { createNumberMask } from 'text-mask-addons';

// libraries
import type { MaskingConfig } from '@interstate/components/TextInput/Types/textInputBaseTypes';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';
import type {
    DrivetrainTypeEnum,
    FuelTypesEnum
} from '@makemydeal/dr-shared-ui-utils/vehicleDealExchangeValidator/types/vehicleTypes';

// styles
import { VehicleInfoGrid, VehicleInfoItemContainer } from './Style';

// components
import { FormTextInput } from '../formManager/FormTextInput';
import { LocationItemRow } from './LocationItemRow';
import { useSelector } from 'react-redux';
import { FormCheckBox } from '../formManager/FormCheckBox';
import { FormRadioButtonList } from '../formManager/FormRadioButtonList';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { FMField } from '../types';
import { FormSelectInput } from '../formManager/FormSelectInput';
import { PrimaryUseFields } from './PrimaryUseFields';

export type VehicleInfoProps = {
    location: {
        isOnLot: boolean;
        gps?: string;
        lot?: string;
    };
};

export const VehicleInfo: FC<VehicleInfoProps> = ({ location }) => {
    const theme = useInterstateTheme();
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);

    const fuelTypes: { label: string; value: FuelTypesEnum }[] = [
        { label: 'Gasoline', value: 'Gasoline' },
        { label: 'Diesel', value: 'Diesel' },
        { label: 'Electric', value: 'Electric' },
        { label: 'Natural Gas', value: 'NaturalGas' },
        { label: 'Hydrogen', value: 'Hydrogen' },
        { label: 'Other', value: 'Other' }
    ];

    const brakingMechanismOptions: { label: string; value: string }[] = [
        { label: 'Power', value: 'Power' },
        { label: 'Manual', value: 'Manual' }
    ];

    const steeringMechanismOptions: { label: string; value: string }[] = [
        { label: 'Power', value: 'Power' },
        { label: 'Manual', value: 'Manual' }
    ];

    const transmissionTypeOptions: { label: string; value: paymentServicesTypes.TransmissionType }[] = [
        { label: 'Automatic', value: 'Automatic' },
        { label: 'Manual', value: 'Manual' }
    ];

    const airConditioningOptions: { label: string; value: string }[] = [
        { label: 'Included', value: 'true' },
        { label: 'Not Included', value: 'false' }
    ];

    const drivelineOptions: { label: string; value: DrivetrainTypeEnum }[] = [
        { label: 'All Wheel Drive', value: 'AllWheelDrive' },
        { label: 'Four Wheel Drive', value: 'FourWheelDrive' },
        { label: 'Front Wheel Drive', value: 'FrontWheelDrive' },
        { label: 'Rear Wheel Drive', value: 'RearWheelDrive' }
    ];

    const mileageMaskDetails = {
        prefix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ',',
        decimalSymbol: '.',
        requireDecimal: false,
        allowLeadingZeroes: false
    };

    const cylindersMaskDetails = {
        prefix: '',
        includeThousandsSeparator: false,
        integerLimit: 2,
        allowDecimal: false,
        allowLeadingZeroes: false
    };

    const gvwrMaskDetails = {
        prefix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ',',
        integerLimit: 7,
        allowDecimal: true,
        decimalSymbol: '.',
        decimalLimit: 2,
        requireDecimal: false,
        allowLeadingZeroes: false
    };

    const masking: MaskingConfig = {
        guide: true,
        mask: createNumberMask(mileageMaskDetails),
        unmask: [',']
    };

    const cylindersMasking: MaskingConfig = {
        guide: true,
        mask: createNumberMask(cylindersMaskDetails),
        unmask: []
    };

    const gvwrMasking: MaskingConfig = {
        guide: true,
        mask: createNumberMask(gvwrMaskDetails),
        unmask: [',']
    };

    return (
        <VehicleInfoItemContainer theme={theme}>
            <VehicleInfoGrid isVerticalMarginsRequired={true} position="relative">
                <PrimaryUseFields />
                <LocationItemRow label="Location" location={location} />
                <FormTextInput label="Exterior Color" name={FMField.exteriorColor} />
                <FormTextInput label="Interior Color" name={FMField.interiorColor} />
                <FormSelectInput label="Fuel" options={fuelTypes} name={FMField.fuel} disable={true} />
                <FormTextInput label="Engine" name={FMField.engine} />
                <FormSelectInput label="Braking Mechanism" options={brakingMechanismOptions} name={FMField.brakingMechanism} />
                <FormSelectInput label="Transmission Type" options={transmissionTypeOptions} name={FMField.transmissionType} />
                <FormTextInput label="Transmission" name={FMField.transmission} />
                <FormSelectInput label="Steering Mechanism" options={steeringMechanismOptions} name={FMField.steeringMechanism} />
                <FormSelectInput label="Driveline" name={FMField.driveLine} options={drivelineOptions} disable={true} />
                <FormTextInput name={FMField.cylinders} label="Cylinders" masking={cylindersMasking} />
                <VehicleInfoGrid isVerticalMarginsRequired={false} gridTemplateColumns="1fr auto">
                    <FormTextInput
                        name={FMField.mileage}
                        label="Odometer"
                        inputSuffix={'mi'}
                        masking={masking}
                        sx={isGCDynamicValidationsEnabled ? { '& .ids-input-message-block': { position: 'absolute' } } : {}}
                    />
                    <FormCheckBox
                        name={FMField.odometerMileageType}
                        label="Actual"
                        sx={{
                            '.MuiFormLabel-root': { fontSize: '1rem' },
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            paddingTop: '1.5rem'
                        }}
                    />
                </VehicleInfoGrid>
                <FormRadioButtonList
                    label="Air Conditioning"
                    name={FMField.isAirConditioningIncluded}
                    options={airConditioningOptions}
                    inline
                />
                <FormTextInput name={FMField.grossVehicleWeightRating} label="Gross Weight Rating" masking={gvwrMasking} />
            </VehicleInfoGrid>
        </VehicleInfoItemContainer>
    );
};
